import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  Ref,
  nextTick,
} from 'vue';
import { debounce } from 'lodash';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { PDFDocumentProxy } from 'pdfjs-dist/legacy/build/pdf';
import { useDebounceFn } from '@vueuse/core';
import PdfPage from '@/utils/pdf/PdfPage';
import { getPageClosestToContainerCenter, getPendingAnchorFromContainerCenter } from '@/utils/pdf/viewportUtils';
import usePdfActions from './usePdfActions';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

type FittingMode = 'width' | 'height';

const usePdf = (pdfCanvasContainer: Ref<HTMLDivElement | null>, pdfCanvasWrapper: Ref<HTMLDivElement | null>) => {
  const { downloadPdf, printPdf } = usePdfActions();

  const heightIsMaximized = ref<boolean>(true);
  const numPages = ref<number>(0);
  const pages = new Map<number, PdfPage>();
  const shouldFitToContainerOnResize = ref(false);
  const fittingMode = ref<FittingMode>('width');
  const isProgrammaticFitTo = ref(false);
  const isRendering = ref(false);

  let resizeObserver: ResizeObserver | null = null;

  const pdf = ref<PDFDocumentProxy | null>(null);

  const scale = ref<number>(1.0);

  type PendingAnchor = { pageNum: number; relX: number; relY: number } | null;
  let pendingAnchor: PendingAnchor = null;

  // #region PDF rendering

  const dpr = computed(() => window.devicePixelRatio || 1);

  const schedulePageRender = async (page: PdfPage, centerAfterRender = false) => {
    await page.renderPage(dpr.value);

    page.isDirty = false;

    if (!centerAfterRender || !pdfCanvasContainer.value || pendingAnchor) return;

    const closestPage = getPageClosestToContainerCenter(pages, pdfCanvasContainer.value);
    if (closestPage) {
      closestPage.getCanvas()?.scrollIntoView();
    }
  };

  const renderPages = async (centerAfterRender = false) => {
    if (!pdf.value) return;

    const container = pdfCanvasContainer.value;
    const canvasWrapper = pdfCanvasWrapper.value;
    if (!container || !canvasWrapper) return;

    isRendering.value = true;

    const containerTop = container.scrollTop;
    const containerBottom = container.scrollTop + container.clientHeight;

    const buffer = container.clientHeight;
    const extendedTop = containerTop - buffer;
    const extendedBottom = containerBottom + buffer;

    const pagesToRender: PdfPage[] = [];

    pages.forEach((page) => {
      const canvas = page.getCanvas();
      if (!canvas) return;
      const canvasTop = canvas.offsetTop;
      const canvasBottom = canvas.offsetTop + canvas.offsetHeight;

      const isInBuffer = canvasBottom >= extendedTop && canvasTop <= extendedBottom;
      const shouldRender = isInBuffer && (page.canvasIsEmpty() || page.isDirty || page.scale !== scale.value);

      if (shouldRender) {
        pagesToRender.push(page);
      }
    });

    if (pagesToRender.length > 0) {
      const MAX_CONCURRENT_RENDERS = 4;
      const renderPromises = pagesToRender.map((page) => schedulePageRender(page, centerAfterRender && page === pagesToRender[0]));

      for (let i = 0; i < renderPromises.length; i += MAX_CONCURRENT_RENDERS) {
        const batch = renderPromises.slice(i, i + MAX_CONCURRENT_RENDERS);
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(batch);
      }
    }

    isRendering.value = false;
  };

  const dirtyAllPages = (newScale: number) => {
    pages.forEach((page) => {
      page.isDirty = true;
      page.scale = newScale;
    });
  };

  // #endregion

  // #region Fit to height or width

  const computeFitScale = (mode: FittingMode, container: HTMLDivElement, page: PdfPage) => {
    const viewport = page.pdfPage.getViewport({ scale: 1 });
    if (mode === 'width') return container.clientWidth / viewport.width;
    return container.clientHeight / viewport.height;
  };

  const applyFitScale = (scaleValue: number, mode: FittingMode) => {
    scale.value = scaleValue;
    heightIsMaximized.value = mode === 'height';
    fittingMode.value = mode;
    shouldFitToContainerOnResize.value = true;
    dirtyAllPages(scaleValue);
  };

  const scrollToPendingAnchor = (pages: Map<number, PdfPage>, container: HTMLDivElement, anchor: PendingAnchor) => {
    if (!anchor) return;
    const canvas = pages.get(anchor.pageNum)?.getCanvas();
    if (!canvas) return;

    const targetX = canvas.offsetLeft + canvas.clientWidth * anchor.relX;
    const targetY = canvas.offsetTop + canvas.clientHeight * anchor.relY;

    const scrollLeft = Math.max(0, Math.min(targetX - container.clientWidth / 2, container.scrollWidth - container.clientWidth));
    const scrollTop = Math.max(0, Math.min(targetY - container.clientHeight / 2, container.scrollHeight - container.clientHeight));

    container.scrollLeft = scrollLeft;
    container.scrollTop = scrollTop;
  };

  const debouncedFitTo = useDebounceFn(async (mode: FittingMode) => {
    if (!pdf.value || !pdfCanvasContainer.value) return;
    isProgrammaticFitTo.value = true;

    pendingAnchor = getPendingAnchorFromContainerCenter(pages, pdfCanvasContainer.value);

    // When loading for the first time (all canvases empty), anchor to page 1
    const anyRendered = Array.from(pages.values()).some((p) => !p.canvasIsEmpty());
    let anchorPage = getPageClosestToContainerCenter(pages, pdfCanvasContainer.value);
    if (!anyRendered) {
      anchorPage = pages.get(1) ?? anchorPage;
      pendingAnchor = { pageNum: anchorPage?.pageNum ?? 1, relX: 0, relY: 0 };
    }

    if (!anchorPage) return;

    const newScale = computeFitScale(mode, pdfCanvasContainer.value, anchorPage);
    applyFitScale(newScale, mode);

    await renderPages(false);

    // Snap the selected page's top-left corner to the container's top-left
    const container = pdfCanvasContainer.value;
    const canvas = pages.get(anchorPage.pageNum)?.getCanvas();
    if (container && canvas) {
      container.scrollLeft = Math.max(0, Math.min(canvas.offsetLeft, container.scrollWidth - container.clientWidth));
      container.scrollTop = Math.max(0, Math.min(canvas.offsetTop, container.scrollHeight - container.clientHeight));
    }

    pendingAnchor = null;

    setTimeout(() => { isProgrammaticFitTo.value = false; }, 200);
  }, 100);

  const fitTo = async (mode: FittingMode) => {
    await debouncedFitTo(mode);
  };

  const fitToContainer = () => fitTo(fittingMode.value);

  const maximizeHeightOrWidth = async () => {
    // Add a small delay to ensure layout is settled
    setTimeout(async () => {
      if (heightIsMaximized.value) {
        await fitTo('width');
      } else {
        await fitTo('height');
      }
    }, 100);
  };

  // #endregion

  // #region Zoom handling

  const MAX_ZOOM = 5.0;
  const MIN_ZOOM = 0.2;

  const maxZoomReached = computed(() => scale.value >= MAX_ZOOM);
  const minZoomReached = computed(() => scale.value <= MIN_ZOOM);

  const debouncedRender = useDebounceFn(async () => {
    const container = pdfCanvasContainer.value;
    if (!container) return;

    // Hide all canvases before rendering
    pages.forEach((page) => {
      const canvas = page.getCanvas();
      if (!canvas) return;
      canvas.style.opacity = '0';
      canvas.style.visibility = 'hidden';
    });

    await renderPages(false);

    // Scroll to pending anchor if it exists
    if (pendingAnchor) {
      scrollToPendingAnchor(pages, container, pendingAnchor);
      pendingAnchor = null;
    }

    // Restore visibility
    pages.forEach((page) => {
      const canvas = page.getCanvas();
      if (!canvas) return;
      canvas.style.opacity = '1';
      canvas.style.visibility = 'visible';
    });
  }, 50);

  const getAnchorPageForZoom = (pages: Map<number, PdfPage>, container: HTMLDivElement) => {
    let anchorPage: PdfPage | null = null;
    pages.forEach((page) => {
      const canvas = page.getCanvas();
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const cx = containerRect.left + containerRect.width / 2;
      const cy = containerRect.top + containerRect.height / 2;
      if (cx >= rect.left && cx <= rect.right && cy >= rect.top && cy <= rect.bottom) {
        anchorPage = page;
      }
    });
    return anchorPage ?? getPageClosestToContainerCenter(pages, container) ?? null;
  };

  const getClampedRelativePosition = (container: HTMLDivElement, canvas: HTMLCanvasElement) => {
    const canvasRect = canvas.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const relX = (containerRect.left + containerRect.width / 2 - canvasRect.left) / canvasRect.width;
    const relY = (containerRect.top + containerRect.height / 2 - canvasRect.top) / canvasRect.height;
    return {
      relX: Math.max(0, Math.min(1, relX)),
      relY: Math.max(0, Math.min(1, relY))
    };
  };

  /**
   * Zoom the PDF canvas while keeping the center of the page in the same position
   * @param amount - The amount to zoom by
   */
  const zoom = async (amount: number) => {
    const container = pdfCanvasContainer.value;
    if (!container || !pdf.value) return;

    const oldScale = scale.value;
    const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, oldScale + amount));
    if (oldScale === newScale) return;

    const anchorPage = getAnchorPageForZoom(pages, container);
    if (!anchorPage) return;

    const roundedScale = Math.round(newScale * 100) / 100;
    scale.value = roundedScale;
    dirtyAllPages(roundedScale);
    shouldFitToContainerOnResize.value = false;

    const anchorPageCanvas = anchorPage.getCanvas();
    if (!anchorPageCanvas) return;

    pendingAnchor = { pageNum: anchorPage.pageNum, ...getClampedRelativePosition(container, anchorPageCanvas) };

    debouncedRender();
  };

  const zoomIn = () => zoom(0.2);

  const zoomOut = () => zoom(-0.2);

  const handleResize = debounce(() => {
    if (shouldFitToContainerOnResize.value && !isProgrammaticFitTo.value) {
      fitToContainer();
    }
  }, 50);

  // #endregion

  // #region PDF loading
  const base64ToBinary = (base64: string) => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  };

  const loadPdf = async (pdfBase64: string, signal: AbortSignal) => {
    if (!pdfCanvasWrapper.value) {
      console.error('pdfCanvasWrapper is not set');
      return;
    }

    const pdfBytes = base64ToBinary(pdfBase64);
    const loadingTask = pdfjsLib.getDocument(pdfBytes);

    signal.addEventListener('abort', () => {
      loadingTask.destroy();
    });

    pdf.value = await loadingTask.promise;

    if (signal.aborted) return;

    numPages.value = pdf.value.numPages;

    for (let pageNum = 1; pageNum <= numPages.value; pageNum++) {
      // eslint-disable-next-line no-await-in-loop
      pages.set(pageNum, new PdfPage(pageNum, await pdf.value.getPage(pageNum), pdfCanvasWrapper.value));
    }

    if (signal.aborted) return;

    // Add a small delay to ensure layout is settled
    setTimeout(() => {
      fitTo('width');
    }, 100);
  };

  const resetPdf = () => {
    if (!pdfCanvasWrapper.value) return;
    pdfCanvasWrapper.value.innerHTML = '';

    pdf.value = null;
    numPages.value = 0;
    scale.value = 1.0;
    isRendering.value = false;

    pages.clear();
  };

  // #endregion

  // #region Scroll and resize handling

  let ticking = false;

  // ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onResize = (entries: ResizeObserverEntry[]) => {
    handleResize();
  };

  const onScroll = () => {
    if (!ticking && !isRendering.value) {
      window.requestAnimationFrame(() => {
        renderPages();
        ticking = false;
      });
      ticking = true;
    }
  };

  // #endregion

  onMounted(async () => {
    await nextTick();
    const container = pdfCanvasContainer.value;
    if (!container) return;

    container.addEventListener('scroll', onScroll);

    resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(container);
  });

  onUnmounted(() => {
    resizeObserver?.disconnect();
    pdfCanvasContainer.value?.removeEventListener('scroll', onScroll);
  });

  return {
    downloadPdf,
    fitTo,
    fitToContainer,
    heightIsMaximized,
    loadPdf,
    maximizeHeightOrWidth,
    maxZoomReached,
    minZoomReached,
    pdf,
    printPdf,
    resetPdf,
    zoomIn,
    zoomOut,
  };
};

export default usePdf;
